import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../utils/axios';
import { getImageUrl } from '../config/apiConfig';

const FranchiseHeroSlider = () => {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchImages();
  }, []);

  useEffect(() => {
    if (images.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, 5000); // Change slide every 5 seconds

      return () => clearInterval(interval);
    }
  }, [images.length]);

  const fetchImages = async () => {
    try {
      console.log('🖼️ Fetching franchise hero images...');
      const response = await api.get('/franchise-hero-images');
      console.log('✅ API Response:', response.data);
      setImages(response.data.data);
      console.log('📸 Images set:', response.data.data);
    } catch (error) {
      console.error('❌ Failed to fetch hero images:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Using getImageUrl from apiConfig

  if (loading) {
    return (
      <div className="relative h-96 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="relative h-96 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-2xl font-bold mb-2">Join the OSOPCoding Revolution</h2>
          <p className="text-lg opacity-90">Become a part of India's fastest-growing tech education franchise</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
      {/* Image Slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="absolute inset-0"
        >
          <img
            src={getImageUrl(images[currentIndex]?.image_url)}
            alt={images[currentIndex]?.title || 'Franchise Hero'}
            className="w-full h-full object-cover"
            onLoad={() => {
              console.log('🖼️ Image loaded successfully:', getImageUrl(images[currentIndex]?.image_url));
            }}
            onError={(e) => {
              console.error('❌ Image failed to load:', getImageUrl(images[currentIndex]?.image_url));
              console.error('❌ Original image_url:', images[currentIndex]?.image_url);
              // Safely handle the error without accessing potentially invalid DOM nodes
              if (e && e.target && e.target.src) {
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04MCAxMDBDODAgODkuNTQ0NyA4OC4wMDAxIDgxLjUgOTguNTU1NiA4MS41SDk5LjQ0NDRDMTEwIDgxLjUgMTE4IDg5LjU0NDcgMTE4IDEwMFYxMDBDMTE4IDExMC40NTUgMTEwIDExOC41IDk5LjQ0NDQgMTE4LjVIOTguNTU1NkM4OC4wMDAxIDExOC41IDgwIDExMC40NTUgODAgMTAwVjEwMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHBhdGggZD0iTTg4IDk2Qzg4IDkyLjY4NjMgOTAuNjg2MyA5MCA5NCA5MEg5NEM5Ny4zMTM3IDkwIDEwMCA5Mi42ODYzIDEwMCA5NlY5NkMxMDAgOTkuMzEzNyA5Ny4zMTM3IDEwMiA5NCAxMDJIOG5kQzkwLjY4NjMgMTAyIDg4IDk5LjMxMzcgODggOTZWNjZaIiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPgo=';
              }
            }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-all duration-300 z-10"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-all duration-300 z-10"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-white scale-125'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Slide Counter */}
      {images.length > 1 && (
        <div className="absolute top-4 right-4 bg-black/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium z-10">
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
};

export default FranchiseHeroSlider; 